
import lottie from 'lottie-web';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { toPercentTime, toPercentTimeDesc } from '../../../common/utils/convertUtil';
import { isAndroid, isIOS, isSbsApp } from '../../../common/utils/passUtil';

const CurrentProgram = React.memo(
  ({
    type,
    data,
    alarms,
    channelName,
    channelId,
    jwtToken,
    onAlarmToggle,
    formatDateTime,

  }) => {

    const [localAlarmState, setLocalAlarmState] = useState({});

    useEffect(() => {
      if (!jwtToken) {
        setLocalAlarmState({});
      } else {
        setLocalAlarmState(alarms);
      }
    }, [alarms, jwtToken]);

    useEffect(() => {
      // 다크모드 
      fetch('https://static.cloud.sbs.co.kr/libs/lottie/live_icon_ani.json')
        .then(response => response.json())
        .then(animationData => {
          // 흰색 배경 레이어 제거
          if (animationData.layers) {
            animationData.layers = animationData.layers.filter(layer => {
              // "Screen" 레이어 제거 (흰색 배경)
              return layer.nm !== 'Screen';
            });
          }

          lottie.loadAnimation({
            container: document.querySelector('.icon_live_on'),
            renderer: 'svg',
            loop: true,
            autoplay: true,
            animationData: animationData // 수정된 데이터 사용
          });
        })
        .catch(error => {
          console.error('Lottie JSON 로드 실패:', error);
          // 실패시 원본 사용
          lottie.loadAnimation({
            container: document.querySelector('.icon_live_on'),
            renderer: 'svg',
            loop: true,
            autoplay: true,
            path: 'https://static.cloud.sbs.co.kr/libs/lottie/live_icon_ani.json'
          });
        });
    }, []);

    const [swiperRef, setSwiperRef] = useState(null);

    useEffect(() => {
      const timeout = setTimeout(() => {
        if (swiperRef && swiperRef.slideTo && data.length > 1) {
          swiperRef.slideTo(1, 0);
        }
      }, 100);

      return () => clearTimeout(timeout);
    }, [type, data, swiperRef]);

    const getAppVersion = () => {
      const match = navigator.userAgent.match(/SBSApp\/([\d.]+)/);
      return match ? match[1] : null;
    };

    const isLowerVersion = (current, target) => {
      const currentParts = current.split('.').map(Number);
      const targetParts = target.split('.').map(Number);

      for (let i = 0; i < targetParts.length; i++) {
        const curr = currentParts[i] || 0;
        const targ = targetParts[i] || 0;
        if (curr < targ) return true;
        if (curr > targ) return false;
      }
      return false;
    };

    // 이전 프로그램 재생 (웹 - 웹 플템다시보기, 앱 - VOD탭)
    const handleClickPlay = (program) => {
      const {
        vod_url,
        programId,
        title,
      } = program;

      const isRadio = ['S17', 'S18', 'S19'].includes(channelId);
      const encodedTitle = encodeURIComponent(title);

      let re_link = '';

      if (!isSbsApp()) {
        // 앱이 아닐 경우
        const appLink = isRadio
          ? (isIOS()
            ? 'gorealra://'
            : 'intent://main?type=link#Intent;scheme=gorealra;package=com.appgate.gorealra;S.market_referrer=sharepage;end')
          : (programId
            ? (isIOS()
              ? `siapp://programhome?programid=${programId}&programtitle=${encodedTitle}&tab=vod`
              : `intent://programhome?programid=${programId}&programtitle=${encodedTitle}&tab=vod#Intent;scheme=siapp;package=kr.co.sbs.videoplayer;end`)
            : vod_url);

        const appStoreLink = isRadio
          ? (isIOS()
            ? 'https://apps.apple.com/kr/app/id340072873' // 고릴라 iOS 앱스토어
            : 'https://play.google.com/store/apps/details?id=com.appgate.gorealra') // 고릴라 안드로이드
          : (isIOS()
            ? 'https://itunes.apple.com/kr/app/id1020136055?mt=8' // SBS iOS
            : 'https://play.google.com/store/apps/details?id=kr.co.sbs.videoplayer'); // SBS 안드로이드

        // 앱 설치 시도 → 미설치 시 앱스토어 유도
        const handleAppInstallCheck = (callback) => {
          const installCheck = setInterval(() => {
            if (document.hidden) {
              clearInterval(installCheck);
              clearTimeout(fallbackTimer);
            }
          }, 200);

          const fallbackTimer = setTimeout(() => {
            callback();
            clearInterval(installCheck);
          }, 3000);
        };

        if (isIOS()) {
          handleAppInstallCheck(() => {
            re_link = appStoreLink;
          });
        } else if (isAndroid()) {
          handleAppInstallCheck(() => {
            re_link = 'https://play.google.com/store/apps/details?id=kr.co.sbs.videoplayer';
          });
        } else {
          re_link = vod_url;
        }
      } else {
        // SBS 앱인 경우
        const appVersion = getAppVersion();

        // play 개편 전 버전 체크 
        const isLowVersionCheck = appVersion ? isLowerVersion(appVersion, '3.0.0') : false;

        if (isLowVersionCheck) {
          re_link = vod_url;
        } else {
          if (isRadio) {
            re_link = isIOS()
              ? 'gorealra://'
              : 'intent://main?type=link#Intent;scheme=gorealra;package=com.appgate.gorealra;S.market_referrer=sharepage;end';
          } else if (programId) {
            re_link = isIOS()
              ? `siapp://programhome?programid=${programId}&programtitle=${encodedTitle}&tab=vod`
              : `intent://programhome?programid=${programId}&programtitle=${encodedTitle}&tab=vod#Intent;scheme=siapp;package=kr.co.sbs.videoplayer;end`;
          } else {
            re_link = vod_url;
          }
        }
      }

      if (re_link) {
        window.location.href = re_link;
      }
    };

    const checkAlarmStatus = useCallback((program) => {
      if (!jwtToken) return false;
      const yyyymmdd_hhmm = formatDateTime(program.starttime_date);
      const alarmKey = `${channelId}_${yyyymmdd_hhmm}`;
      return !!localAlarmState[alarmKey];
    }, [localAlarmState, channelId, jwtToken, formatDateTime]);

    const handleClickAlarm = useCallback(
      (program) => (event) => {
        event.preventDefault();

        const {
          programId,
          title,
          starttime_date,
          circle_image,
        } = program;

        const circleImage = circle_image || '';
        const date = formatDateTime(starttime_date);
        const alarmKey = `${channelId}_${date}`;

        window.__SBSLogModuleDataLayer = window.__SBSLogModuleDataLayer || [];
        window.__SBSLogModuleDataLayer.push({
          type: 'click',
          option: {
            clickName: '라이브/예약',
            clickType: 'NC',
            value1: `${channelName}/${title}`,
            value2: '',
            value3: '',
            value4: '',
            value5: '',
          },
        });

        const isAlarmActive = localAlarmState[alarmKey] ?? false;

        if (isSbsApp()) {
          if (!jwtToken) {
            if (/android/i.test(window.navigator.userAgent)) {
              window.location.href = `intent://login?type=schedule&channelid=${channelId}&date=${date}&img=${encodeURIComponent(circleImage)}#Intent;scheme=siapp;package=kr.co.sbs.videoplayer;end`;
            } else {
              window.location.href = `siapp://login?type=schedule&channelid=${channelId}&date=${date}&img=${encodeURIComponent(circleImage)}`;
            }
            return; // 비로그인인 경우 
          } else {
            if (/android/i.test(window.navigator.userAgent)) {
              window.location.href = `intent://schedule?channelid=${channelId}&date=${date}&img=${encodeURIComponent(circleImage)}&programtitle=${encodeURIComponent(title)}#Intent;scheme=siapp;package=kr.co.sbs.videoplayer;end`;
            } else {
              window.location.href = `siapp://schedule?channelid=${channelId}&date=${date}&img=${encodeURIComponent(circleImage)}&programtitle=${encodeURIComponent(title)}`;
            }
          }

          setLocalAlarmState(prev => ({
            ...prev,
            [alarmKey]: !isAlarmActive,
          }));
        } else {
          if (confirm(`라이브 예약은 앱에서만 가능합니다. 'SBS play' 에서 열겠습니까?`)) {
            window.location.href = 'siapp://home?tab=home';
          }
        }
      },
      [jwtToken, channelId, channelName, formatDateTime, setLocalAlarmState, localAlarmState]
    );


    return (
      <>
        <div className="now_program_w">
          <Swiper
            onSwiper={setSwiperRef}
            slidesPerView="auto"
            centeredSlides={true}
            loop={false}
            slidesPerGroup={1}
            spaceBetween={10}
            pagination={false}
            navigation={false}
            className="listModuleSlider"
            wrapperClass="SliderContent"
            onSlideChange={() => {
            }}
            style={{ touchAction: 'pan-y' }}
          >
            {data.map((program, idx) => {
              let progressPercent =
                program.starttime && program.endtime
                  ? toPercentTime(program.starttime, program.endtime)
                  : null;
              progressPercent =
                progressPercent > 100 || progressPercent < 0
                  ? null
                  : progressPercent;
              const progressDesc = progressPercent
                ? toPercentTimeDesc(program.starttime, program.endtime, progressPercent)
                : '';

              const isAlarmActive = checkAlarmStatus(program);

              return (
                <SwiperSlide key={idx} className="now_program_inner">
                  <div className={`schedule_box_w ${program.status === 'past' ? 'before' :
                    program.status === 'now' ? 'current' :
                      program.status === 'upcoming' ? 'after' :
                        ''
                    }`}>
                    <div className="schedule_box_inner">
                      <div className="schedule_box_title">
                        {program.status === 'now' && <span className="icon_live_on"></span>}
                        {program.title}
                      </div>
                      <div className="schedule_box_time">{program.starttime} ~ {program.endtime}</div>
                      <div className="schedule_box_channel">{channelName}</div>
                      <div className="schedule_box_progress_w">
                        <i className="hide">총시간 및 진행시간</i>
                        <span
                          className="sb_progress"
                          style={{ width: progressPercent !== null ? `${progressPercent}%` : '0%' }}
                          title={progressDesc}
                        ></span>
                      </div>

                      {program.status === 'upcoming' ? (
                        <button
                          id={`alarm_${channelId}_${program.starttime_date}`}
                          type="button"
                          onClick={handleClickAlarm(program)}
                          className={`btnIcon01_alarm_toggle ${
      checkAlarmStatus(program) ? 'active' : ''
    }`}
                        >
                          <span className="ico_alarm_on">
                            <i className="ir">알림 받기 중</i>
                          </span>
                          <span className="ico_alarm_off">
                            <i className="ir">알림 받기 전</i>
                          </span>
                        </button>

                      ) : type === 'tv' && program.status === 'past' && program.programId ? (
                        <span
                          className="ico_play"
                          onClick={(e) => {
                            e.preventDefault();
                            handleClickPlay(program);
                          }}
                        >
                          <i className="ir">재생</i>
                        </span>
                      ) : null}
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      </>
    );
  }
);

export default CurrentProgram;